import type { ErrorService } from '#src/telemetry/errors/error_service.ts';
import { invariant } from '@packages/primitives/invariant';
import type { z } from 'zod';

export type AnalyticsValue = string | number | boolean | null;
export type AnalyticsProperties = Record<string, AnalyticsValue>;

export interface AnalyticsEvent<Schema extends AnalyticsEventSchema = AnalyticsEventSchema> {
  name: string;
  schema: Schema;
  propertyNames?: AnalyticsPropertyNames<Schema>;
}

export type AnalyticsEventSchema = z.ZodType<AnalyticsProperties>;

export type AnalyticsPropertyNames<Schema extends AnalyticsEventSchema> = {
  readonly [Property in keyof z.output<Schema>]: string;
};

export interface EventCapturer {
  <Schema extends AnalyticsEventSchema>(
    event: AnalyticsEvent<Schema>,
    properties: z.input<Schema>,
  ): void;
}

export function defineEvent<const Schema extends AnalyticsEventSchema>(
  name: string,
  schema: Schema,
): AnalyticsEvent<Schema>;
export function defineEvent<const Schema extends AnalyticsEventSchema>(
  name: string,
  schema: Schema,
  propertyNames: AnalyticsPropertyNames<Schema>,
): AnalyticsEvent<Schema>;
export function defineEvent<const Schema extends AnalyticsEventSchema>(
  name: string,
  schema: Schema,
  propertyNames?: AnalyticsPropertyNames<Schema>,
): AnalyticsEvent<Schema> {
  return { name, schema, propertyNames };
}

export function createEventCapturer(
  errorService: Pick<ErrorService, 'captureWarning'>,
  eventTransport: EventTransport,
): EventCapturer {
  return function captureEvent<Schema extends AnalyticsEventSchema>(
    event: AnalyticsEvent<Schema>,
    properties: z.input<Schema>,
  ): void {
    const validation = event.schema.safeParse(properties);
    if (!validation.success) {
      errorService.captureWarning(`Invalid analytics event properties for ${event.name}`);
      return;
    }

    eventTransport(event.name, mapPropertyNames(event, validation.data));
  };
}

type EventTransport = (event: string, properties: AnalyticsProperties) => void;

function mapPropertyNames<Schema extends AnalyticsEventSchema>(
  event: AnalyticsEvent<Schema>,
  properties: z.output<Schema>,
): AnalyticsProperties {
  if (event.propertyNames === undefined) {
    return properties;
  }

  const mappedProperties: AnalyticsProperties = {};
  for (const [property, analyticsName] of Object.entries(event.propertyNames)) {
    const value = properties[property];
    invariant(value !== undefined, `Validated analytics property is missing: ${property}`);
    mappedProperties[analyticsName] = value;
  }
  return mappedProperties;
}
